import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { Clock } from "lucide-react";

import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Alert, AlertDescription } from "@/components/ui/alert";

import { PaymentWarningBanner } from "@/components/settings/PaymentWarningBanner";
import { AddinInstallBanner } from "@/components/settings/AddinInstallBanner";
import { ThemeSettings } from "@/components/settings/ThemeSettings";
import { SmartCategoriesSettings } from "@/components/settings/SmartCategoriesSettings";
import { SmartNotesSettings } from "@/components/settings/SmartNotesSettings";
import { PresetManager } from "@/components/settings/PresetManager";
import { FeatureUpgradeCard } from "@/components/settings/FeatureUpgradeCard";

import { SettingsLayout, useSettingsSection } from "@/components/settings/SettingsLayout";
import { AccountSection } from "@/components/settings/sections/AccountSection";
import { SubscriptionSection } from "@/components/settings/sections/SubscriptionSection";
import { CreditsSection } from "@/components/settings/sections/CreditsSection";
import { BillingSection } from "@/components/settings/sections/BillingSection";
import { SecuritySection } from "@/components/settings/sections/SecuritySection";
import { ReviewSection } from "@/components/settings/sections/ReviewSection";

import { useAuth } from "@/contexts/AuthContext";
import { useSubscription } from "@/hooks/useSubscription";
import { useFeatureAccess } from "@/hooks/useFeatureAccess";
import { useTestClock } from "@/contexts/TestClockContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const Settings = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const { refresh: refreshSubscription } = useSubscription();
  const { isTestMode, getCurrentTime } = useTestClock();
  const { canUseSmartCategories, canUseSmartNotes, canUseFieldPresets } = useFeatureAccess();

  const [active, setActive] = useSettingsSection("account");
  const [searchParams, setSearchParams] = useSearchParams();
  const [isAdmin, setIsAdmin] = useState(false);


  // Legacy email links use ?tab=review — translate to the new ?section=review
  useEffect(() => {
    if (searchParams.get("tab") === "review") {
      const next = new URLSearchParams(searchParams);
      next.delete("tab");
      next.set("section", "review");
      setSearchParams(next, { replace: true });
      setActive("review");
    }
  }, [searchParams, setSearchParams, setActive]);

  // Admin check
  useEffect(() => {
    (async () => {
      const { checkDatabaseAdminRole } = await import("@/utils/deviceFingerprint");
      const hasAdminRole = await checkDatabaseAdminRole();
      setIsAdmin(hasAdminRole);
    })();
  }, []);

  // Payment / cycle / plan change return handling
  useEffect(() => {
    const paymentStatus = searchParams.get("payment");
    const paymentType = searchParams.get("type");
    const cycleChanged = searchParams.get("cycle_changed");
    const planChanged = searchParams.get("plan_changed");

    const clearParams = () => {
      const next = new URLSearchParams(searchParams);
      ["payment", "type", "session_id", "cycle_changed", "plan_changed"].forEach((k) => next.delete(k));
      setSearchParams(next, { replace: true });
    };

    if (cycleChanged === "success") {
      toast({ title: "Billing cycle changed", description: "Your subscription has been updated." });
      refreshSubscription();
      clearParams();
      return;
    }
    if (planChanged === "success") {
      toast({ title: "Plan changed", description: "Your subscription has been updated." });
      refreshSubscription();
      clearParams();
      return;
    }
    if (paymentStatus === "success") {
      if (paymentType === "topup") {
        toast({ title: "Top-up processing", description: "Your credits will appear shortly." });
        setTimeout(() => refreshSubscription(), 3000);
      } else if (paymentType === "subscription") {
        setTimeout(async () => {
          await refreshSubscription();
          try {
            const { data: subData } = await supabase.functions.invoke("check-subscription");
            if (subData?.subscribed && user?.email) {
              await supabase.functions.invoke("send-transactional-email", {
                body: {
                  to: user.email,
                  toName: user.email.split("@")[0],
                  emailType: "subscription_activated",
                  templateVars: {
                    tier: subData.tier,
                    credits: subData.credits_monthly?.toString(),
                    billingCycle: subData.billing_cycle,
                  },
                },
              });
            }
          } catch (e) {
            console.error("[Settings] Subscription email failed", e);
          }
        }, 2000);
        toast({ title: "Subscription activated", description: "Check your email for confirmation." });
      }
      clearParams();
      // Switch to credits section after a top-up so users see new balance
      if (paymentType === "topup") setActive("credits");
      else if (paymentType === "subscription") setActive("subscription");
    } else if (paymentStatus === "cancelled") {
      toast({
        title: "Payment cancelled",
        description: "Your payment was cancelled. No charges were made.",
        variant: "destructive",
      });
      clearParams();
    }
  }, [searchParams, setSearchParams, toast, refreshSubscription, setActive, user]);

  const renderSection = () => {
    switch (active) {
      case "account":
        return <AccountSection isAdmin={isAdmin} />;
      case "subscription":
        return <SubscriptionSection />;
      case "credits":
        return <CreditsSection />;
      case "billing":
        return <BillingSection />;
      case "smart-categories":
        return canUseSmartCategories ? (
          <SmartCategoriesSettings />
        ) : (
          <FeatureUpgradeCard
            featureName="Smart Categories"
            description="Automatically tag and organize contacts with custom AI-powered categories."
            benefits={["Custom category rules", "Auto-tagging on extraction", "Color-coded organization"]}
          />
        );
      case "smart-notes":
        return canUseSmartNotes ? (
          <SmartNotesSettings />
        ) : (
          <FeatureUpgradeCard
            featureName="Smart Notes"
            description="Generate AI-powered research summaries about every new contact."
            benefits={["Company research summaries", "Custom prompt presets", "Stored as contact notes"]}
          />
        );
      case "field-presets":
        return canUseFieldPresets ? (
          <PresetManager />
        ) : (
          <FeatureUpgradeCard
            featureName="Field Presets"
            description="Define which fields to extract for different contact types."
            benefits={["Custom extraction templates", "Per-context field sets", "Reusable across the team"]}
          />
        );
      case "security":
        return <SecuritySection />;
      case "appearance":
        return <ThemeSettings />;
      case "review":
        return <ReviewSection />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen flex flex-col app-surface">
      <Navbar />
      <div className="flex-1">
        {/* Cross-section notifications above the layout */}
        <div className="border-b border-border/60 bg-background/40">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pt-4 space-y-3">
            {isTestMode && (
              <Alert className="border-primary/40 bg-primary/5">
                <Clock className="h-4 w-4 text-primary" />
                <AlertDescription>
                  <strong>🧪 Test Mode Active:</strong> All dates and times are simulated.
                  <br />
                  <span className="text-xs text-muted-foreground">
                    Current simulated time: {format(getCurrentTime(), "PPP HH:mm:ss")}
                  </span>
                </AlertDescription>
              </Alert>
            )}

            <PaymentWarningBanner />

            <AddinInstallBanner />

          </div>
        </div>

        <SettingsLayout active={active} onChange={setActive} isAdmin={isAdmin}>
          {renderSection()}
        </SettingsLayout>
      </div>
      <Footer />
    </div>
  );
};

export default Settings;
